import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, ButtonGroup, Collapse } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useMyHrStreetTypes } from "my-hr/hooks";
import { OcFormEditorItemList } from "oc/components";
import { useOcFormEditorSelector } from "oc/hooks";

interface OcFormEditorStreetTypeSelectorProps {
  name: string;
  label: string;
  value: { name: any } | string | null;
  onChange: (field: string, value: any) => void;
  error?: string;
  isHungary: boolean;
  isMobile: boolean;
}

const extractName = (value: any): string => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value.name === "string") return value.name;
  if (value.name && typeof value.name.name === "string") return value.name.name;
  return "";
};

export const OcFormEditorStreetTypeSelector: React.FC<
  OcFormEditorStreetTypeSelectorProps
> = ({ name, label, value, onChange, error, isHungary, isMobile }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const normalizedValueName = useMemo(() => extractName(value), [value]);

  const { data: streetTypes } = useMyHrStreetTypes();
  const {
    list,
    search,
    selectedItem,
    handleSearch,
    handleClearSearch,
    handleHasMore,
  } = useOcFormEditorSelector({
    value: normalizedValueName,
    list: streetTypes,
    selectName: true,
  });

  const shortList = ["utca", "út", "köz", "tér"];
  const isShort = shortList.includes(normalizedValueName);

  const [showList, setShowList] = useState(!isShort);

  useEffect(() => {
    setShowList(!shortList.includes(normalizedValueName));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedValueName]);

  if (!isHungary) return null;

  const handleShortButtonChange = (shortName: string) => {
    setShowList(false);
    onChange("streetType", shortName);
  };

  const handleListChange = (arg1: any, arg2?: any) => {
    const rawSelected = arg2 ?? arg1;
    let resolvedName = "";

    if (typeof rawSelected === "string") {
      const matchedByCode = list?.find((it: any) => it.code === rawSelected);
      const matchedByName = list?.find((it: any) => it.name === rawSelected);
      if (matchedByName) {
        resolvedName = matchedByName.name;
      } else if (matchedByCode) {
        resolvedName = matchedByCode.name;
      } else {
        resolvedName = rawSelected;
      }
    } else if (rawSelected?.name && typeof rawSelected.name === "string") {
      resolvedName = rawSelected.name;
    } else if (rawSelected?.name?.name) {
      resolvedName = rawSelected.name.name;
    } else {
      if (rawSelected?.code) {
        const matched = list?.find((it: any) => it.code === rawSelected.code);
        resolvedName = matched ? matched.name : rawSelected.code;
      }
    }

    if (resolvedName) {
      onChange("streetType", resolvedName);
    }
  };

  return (
    <Box paddingTop="22px">
      <Box>
        <Box
          style={{
            color: theme.palette.gray80.main,
            background: "white",
            fontSize: "12px",
            padding: "0px 8px 8px 8px",
          }}
        >
          {t(label)}
        </Box>
        <ButtonGroup
          orientation={isMobile ? "vertical" : "horizontal"}
          fullWidth={isMobile ? true : false}
        >
          {shortList.map((item, index) => {
            const isSelected = item === normalizedValueName;
            return (
              <Button
                key={`${item}-${index}`}
                name={item}
                variant={isSelected && !showList ? "contained" : "outlined"}
                color={isSelected && !showList ? "primary" : "inherit"}
                disableElevation
                onClick={() => handleShortButtonChange(item)}
              >
                {item}
              </Button>
            );
          })}
          <Button
            variant={showList ? "contained" : "outlined"}
            color={showList ? "primary" : "inherit"}
            disableElevation
            onClick={() => setShowList(true)}
          >
            egyéb
          </Button>
        </ButtonGroup>
      </Box>
      <Collapse in={showList} timeout="auto" unmountOnExit>
        <OcFormEditorItemList
          search={search}
          onSearch={handleSearch}
          onChange={handleListChange}
          onClearSearch={handleClearSearch}
          list={list}
          onScroll={handleHasMore}
          value={selectedItem}
          label={label}
          name={name}
          error={!!error}
          showItem={true}
        />
      </Collapse>
    </Box>
  );
};

export default OcFormEditorStreetTypeSelector;
